import React, { useState, useEffect, useMemo } from "react"
import {
  Container,
  Button,
  Table,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  InputGroupText,
  InputGroup,
  Input,
  FormFeedback,
  Label,
  Form,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap"
import {
  getUserDetailsById,
  sendOTP,
  verifyEmail,
  verifyMobile,
  updateUserDetails,
} from "api/users"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Link } from "react-router-dom"
import Breadcrumb from "../../components/Common/Breadcrumb"
import ChangePasswordModal from "components/Modals/ChangePasswordModal"
import Swal from "sweetalert2"

const Test = () => {
  document.title = "User Details | CA Portal"

  const [userDetailsData, setUserDetailsData] = useState({})
  const [userData, setUserData] = useState({})
  const [modalStandard, setModalStandard] = useState(false)
  const [modalMobile, setModalMobile] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [settingsMenu, setSettingsMenu] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [isEmailVerified, setIsEmailVerified] = useState(false)
  const [isMobileVerified, setIsMobileVerified] = useState(false)
  const [isMfaEnabled, setIsMfaEnabled] = useState(false)

  const id = localStorage.getItem("appUserId")

  // ----- helpers -----
  const showSwal = (icon, title, text) => {
    Swal.fire({ icon, title, text })
  }

  // Reintroduce aliases & roleAliases
  const aliases = {
    id: "ID",
    firstName: "First Name",
    middleName: "Middle Name",
    lastName: "Last Name",
    email: "Email",
    mobile: "Mobile",
    dateOfBirth: "DOB",
    sex: "Gender",
    preferredLanguage: "Preferred Language",
    roles: "Role",
  }

  const roleAliases = {
    ROLE_ADMIN: "Admin",
    ROLE_SUPER_ADMIN: "Super Admin",
    ROLE_ANALYST: "Analyst",
    ROLE_EDITOR: "Editor",
    ROLE_USER: "User",
  }

  const getRoleAlias = roleName => roleAliases[roleName] || roleName

  const toggleStandard = () => setModalStandard(!modalStandard)
  const toggleMobile = () => setModalMobile(!modalMobile)
  const handleUpdateModalToggle = () => setShowUpdateModal(!showUpdateModal)
  const handleDropdownToggle = () => setSettingsMenu(!settingsMenu)

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await getUserDetailsById(id)
        if (response?.status === 200) {
          const formattedData = formatDates(response.data)
          setUserData(response.data)
          setUserDetailsData(formattedData)
          setIsMfaEnabled(!!formattedData.mfaEnabled)
          setIsEmailVerified(!!formattedData.isEmailVerified)
          setIsMobileVerified(!!formattedData.isMobileVerified)
        }
      } catch (error) {
        console.error("Error fetching user details:", error)
      }
    }
    if (id) fetchUserDetails()
  }, [id])

  const formatDate = dateString =>
    dateString
      ? new Date(dateString).toLocaleString(undefined, {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        })
      : ""

  const formatDates = data => ({
    ...data,
    dateOfBirth: data?.dateOfBirth ? formatDate(data.dateOfBirth) : "",
  })

  const updateUserEmailVerification = async enabled => {
    try {
      const userUpdateData = { ...userData, isEmailVerified: enabled }
      await updateUserDetails(id, userUpdateData)
      setIsEmailVerified(enabled)
      showSwal(
        "success",
        "Success",
        `Email verification ${enabled ? "enabled" : "disabled"} successfully.`
      )
    } catch {
      showSwal("error", "Error", "Email verification update failed.")
    }
  }

  const updateUserMobileVerification = async enabled => {
    try {
      const userUpdateData = { ...userData, isMobileVerified: enabled }
      await updateUserDetails(id, userUpdateData)
      setIsMobileVerified(enabled)
      showSwal(
        "success",
        "Success",
        `Mobile verification ${enabled ? "enabled" : "disabled"} successfully.`
      )
    } catch {
      showSwal("error", "Error", "Mobile verification update failed.")
    }
  }

  const updateUserMfaStatus = async enabled => {
    try {
      const userUpdateData = { ...userData, mfaEnabled: enabled }
      await updateUserDetails(id, userUpdateData)
      setIsMfaEnabled(enabled)
      showSwal(
        "success",
        "Success",
        `MFA ${enabled ? "enabled" : "disabled"} successfully.`
      )
    } catch {
      showSwal("error", "Error", "Error updating MFA status.")
    }
  }

  // ----- Formik: Email -----
  const validation = useFormik({
    initialValues: { email: userData.email || "", otp: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Required"),
      otp: Yup.string().required("Required"),
    }),
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        await verifyEmail(id, values)
        await updateUserEmailVerification(true)
        toggleStandard()
      } catch {
        showSwal("error", "Error", "Email verification failed.")
      }
    },
  })

  // ----- Formik: Mobile -----
  const formik = useFormik({
    initialValues: { mobile: userData.mobile || "", otp: "" },
    validationSchema: Yup.object({
      mobile: Yup.string().required("Required"),
      otp: Yup.string().required("Required"),
    }),
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        await verifyMobile(id, values)
        await updateUserMobileVerification(true)
        toggleMobile()
      } catch {
        showSwal("error", "Error", "Mobile verification failed.")
      }
    },
  })

  // ----- OTP senders -----
  const handleSendOTP = async () => {
    try {
      await sendOTP({ email: validation.values.email })
      setCodeSent(true)
      showSwal("success", "Success", "Code sent to your email.")
    } catch {
      setCodeSent(false)
      showSwal("error", "Error", "Error sending code.")
    }
  }

  const handleSendMobileOTP = async () => {
    try {
      await sendOTP({ mobile: formik.values.mobile })
      setCodeSent(true)
      showSwal("success", "Success", "Code sent to your mobile.")
    } catch {
      setCodeSent(false)
      showSwal("error", "Error", "Error sending code.")
    }
  }

  // ----- checkbox handling -----
  const handleCheckboxChange = async e => {
    const { id: elId, checked } = e.target

    if (elId === "emailCheckbox") {
      checked
        ? setModalStandard(true)
        : await updateUserEmailVerification(false)
    } else if (elId === "mobileCheckbox") {
      checked ? setModalMobile(true) : await updateUserMobileVerification(false)
    } else if (elId === "mfaCheckbox") {
      if (!isEmailVerified && !isMobileVerified) {
        showSwal("warning", "Warning", "Verify email or mobile first.")
      } else {
        updateUserMfaStatus(checked)
      }
    }
  }

  // ----- prepare cleanedUserDetails (filter out keys we don't show) -----
  const excludedKeys = useMemo(
    () => [
      "deleted",
      "userType",
      "status",
      "profileId",
      "profileUrl",
      "isEmailVerified",
      "isMobileVerified",
      "joinedForum",
      "forumUserId",
      "mfaEnabled",
      "onseSignalId",
    ],
    []
  )

  const cleanedUserDetails = useMemo(() => {
    const entries = Object.entries(userDetailsData || {}).filter(
      ([key]) => !excludedKeys.includes(key)
    )
    return Object.fromEntries(entries)
  }, [userDetailsData, excludedKeys])

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="d-flex justify-content-between mb-3">
          <Breadcrumb title="Profile" breadcrumbItem="User Details" />
          <Button tag={Link} to="/dashboard" color="primary" className="btn">
            <i className="bx bx-arrow-back"></i> Back
          </Button>
        </div>

        <Container fluid>
          <Card>
            <CardBody>
              <Row className="align-items-center">
                <Col
                  xs={12}
                  className="d-flex justify-content-end align-items-center ml-2"
                >
                  <Dropdown isOpen={settingsMenu} toggle={handleDropdownToggle}>
                    <DropdownToggle tag="button" className="btn btn-primary">
                      <i className="bx bx-align-right" />
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end p-3">
                      <DropdownItem
                        className="d-flex align-items-center py-2"
                        onClick={handleUpdateModalToggle}
                      >
                        <div className="mb-3">
                          <i className="bx bxs-shield-alt-2 me-2"></i>
                          <span>Change Password</span>
                        </div>
                      </DropdownItem>

                      <DropdownItem className="d-flex align-items-center py-2">
                        <div className="form-check form-switch mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="mfaCheckbox"
                            onChange={handleCheckboxChange}
                            checked={isMfaEnabled}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="mfaCheckbox"
                          >
                            Enable Multi-factor Authentication (MFA)
                          </label>
                        </div>
                      </DropdownItem>

                      <DropdownItem className="d-flex align-items-center py-2">
                        <div className="form-check form-switch mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="emailCheckbox"
                            onChange={handleCheckboxChange}
                            checked={isEmailVerified}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="emailCheckbox"
                          >
                            Verify Email
                          </label>
                        </div>
                      </DropdownItem>

                      <DropdownItem className="d-flex align-items-center py-2">
                        <div className="form-check form-switch mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="mobileCheckbox"
                            onChange={handleCheckboxChange}
                            checked={isMobileVerified}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="mobileCheckbox"
                          >
                            Verify Mobile
                          </label>
                        </div>
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </Col>
              </Row>

              <Row>
                <div className="table-responsive">
                  <Table className="table mb-4">
                    <tbody>
                      {Object.entries(cleanedUserDetails).map(
                        ([key, value]) => (
                          <tr key={key}>
                            <td>
                              <strong>{aliases[key] || key}</strong>
                            </td>
                            <td>
                              {key === "roles" && Array.isArray(value)
                                ? value.map(role => (
                                    <span key={role.roleId} className="me-2">
                                      {getRoleAlias(role.roleName)}
                                    </span>
                                  ))
                                : value && typeof value === "object"
                                ? JSON.stringify(value)
                                : value ?? "-"}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </Table>
                </div>
              </Row>
            </CardBody>
          </Card>

          <ChangePasswordModal
            isOpen={showUpdateModal}
            toggle={handleUpdateModalToggle}
            userId={id}
          />

          {/* Email verification modal */}
          <Modal isOpen={modalStandard} toggle={toggleStandard} centered>
            <ModalHeader>Verify Email Address</ModalHeader>
            <ModalBody>
              <Form
                className="form-horizontal"
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                  return false
                }}
              >
                <div className="mb-3">
                  <Label className="form-label">Email</Label>
                  <InputGroup>
                    <Input
                      name="email"
                      className="form-control"
                      placeholder="Enter email"
                      type="email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                      invalid={
                        validation.touched.email && validation.errors.email
                      }
                    />
                    <InputGroupText addonType="append">
                      <Button color="primary" onClick={handleSendOTP}>
                        Send Code
                      </Button>
                    </InputGroupText>
                  </InputGroup>
                  {validation.touched.email && validation.errors.email && (
                    <FormFeedback type="invalid">
                      {validation.errors.email}
                    </FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label className="form-label">Verification Code</Label>
                  <Input
                    name="otp"
                    className="form-control"
                    placeholder="Enter Code"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.otp || ""}
                    invalid={validation.touched.otp && validation.errors.otp}
                  />
                  {validation.touched.otp && validation.errors.otp && (
                    <FormFeedback type="invalid">
                      {validation.errors.otp}
                    </FormFeedback>
                  )}
                </div>

                <Row className="mb-3">
                  <Col className="text-end">
                    {validation.values.otp && (
                      <Button
                        color="primary"
                        type="submit"
                        disabled={
                          !validation.isValid || validation.isSubmitting
                        }
                      >
                        Verify Email
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>

          {/* Mobile verification modal */}
          <Modal isOpen={modalMobile} toggle={toggleMobile} centered>
            <ModalHeader>Verify Phone Number</ModalHeader>
            <ModalBody>
              <Form
                className="form-horizontal"
                onSubmit={e => {
                  e.preventDefault()
                  formik.handleSubmit()
                  return false
                }}
              >
                <div className="mb-3">
                  <Label className="form-label">Mobile</Label>
                  <InputGroup>
                    <Input
                      name="mobile"
                      className="form-control"
                      placeholder="Enter mobile"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.mobile || ""}
                      invalid={formik.touched.mobile && formik.errors.mobile}
                    />
                    <InputGroupText addonType="append">
                      <Button color="primary" onClick={handleSendMobileOTP}>
                        Send Code
                      </Button>
                    </InputGroupText>
                  </InputGroup>
                  {formik.touched.mobile && formik.errors.mobile && (
                    <FormFeedback type="invalid">
                      {formik.errors.mobile}
                    </FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label className="form-label">Verification Code</Label>
                  <Input
                    name="otp"
                    className="form-control"
                    placeholder="Enter Code"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.otp || ""}
                    invalid={formik.touched.otp && formik.errors.otp}
                  />
                  {formik.touched.otp && formik.errors.otp && (
                    <FormFeedback type="invalid">
                      {formik.errors.otp}
                    </FormFeedback>
                  )}
                </div>

                <Row className="mb-3">
                  <Col className="text-end">
                    {formik.values.otp && (
                      <Button
                        color="primary"
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting}
                      >
                        Verify Mobile
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Test
